import _ from 'underscore';
import styled from 'styled-components';
import { getCurrentUser } from '~/common/explicit-exports';
import DropdownEnabledSearchBar from '../search/dropdown-enabled-search-bar';
import AdminIndices from '../../../algolia/admin/admin-indices';
import AdminQuickSearchResult from '../../../algolia/admin/admin-quick-search-result';
import withSearchResultsManagement from '../search/with-search-results-management';
import withAlgoliaSearch from '../search/with-algolia-search';
import isFeatureEnabled from '../is-feature-enabled';

interface Props {
  onItemSelect: (item) => void;
  onFolderSelect: (folder) => void;
  validSearchTypes?: string[];
  includeUnpublishedTrainings?: boolean;
  results: any;
  searching: boolean;
  beforeSearch: () => string;
  search: (query: string, callback: (results: any) => void, options: any) => void;
  clear: () => void;
  filter: (results: any, callback: (filteredResults: any) => void) => void;
  format: (results: any) => any;
  afterSearch: (searchId: string, results: any) => void;
  onlyVisible?: boolean;
}

function AssetPickerSearchBar({
  onItemSelect,
  onFolderSelect,
  validSearchTypes,
  includeUnpublishedTrainings = false,
  ...props
}: Props) {
  const search = (query) => {
    if (query) {
      const searchId = props.beforeSearch();
      const filters = [];
      if (!includeUnpublishedTrainings) {
        filters.push('is_published:true');
      }

      if (validSearchTypes) {
        filters.push(`(${validSearchTypes.map((type) => `type:"${_.escape(type)}"`).join(' OR ')})`);
      }

      props.search(query, (results) => afterSearch(searchId, results), {
        offset: 0,
        length: 50,
        filters: filters.join(' AND '),
        // analyticsTags are used to filter data in search analytics
        analyticsTags: ['internal', 'admin', `enterprise_id:${getCurrentUser().enterpriseId}`],
      });
    } else {
      props.clear();
    }
  };

  const afterSearch = (searchId, results) => {
    props.filter(results, (filteredResults) => {
      let formattedResults = props.format(filteredResults);
      formattedResults = _.flatten(formattedResults);

      if (isFeatureEnabled('fy25_q3_hide_content_from_learners') && props.onlyVisible) {
        formattedResults = formattedResults.filter((result) => !result.searchResult.is_hidden);
      }

      props.afterSearch(searchId, formattedResults);
    });
  };

  const handleItemSelect = (item) => {
    if (item) {
      onItemSelect(item);
      props.clear();
    }
  };

  const handleFolderClick = (folder) => {
    onFolderSelect(folder);
    props.clear();
  };

  const renderAdminQuickSearchResult = (result, isActive) => (
    <AdminQuickSearchResult
      {...result}
      isActive={isActive}
      handleFolderClick={handleFolderClick}
      onSelect={() => handleItemSelect(result)}
    />
  );

  return (
    <DropdownWrapper>
      <DropdownEnabledSearchBar
        allowReset
        placeholder="Search Training"
        handleItemSelect={handleItemSelect}
        renderResult={renderAdminQuickSearchResult}
        results={props.results}
        search={search}
        searching={props.searching}
      />
    </DropdownWrapper>
  );
}

const DropdownWrapper = styled.div`
  .dropdown-enabled-search-bar {
    .search-bar {
      justify-content: flex-start;
      border: 1px solid #d3d3d3;
      border-radius: 4px;
      font-size: 14px;
      height: 32px;

      input {
        width: 100%;
      }
    }

    .search-bar-dropdown {
      max-height: 60vh;
    }
  }
`;

export const AcademyAssetPickerSearchBar = withSearchResultsManagement(
  withAlgoliaSearch(AssetPickerSearchBar, AdminIndices.getAllForAcademy()),
);

export default withSearchResultsManagement(withAlgoliaSearch(AssetPickerSearchBar, AdminIndices.getAll()));
